import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { scListSifs, scCompare } from "../../lib/sifcontrolApi";

const fmt = (v, unit = "", digits = 2) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return unit === "%" ? `${n.toFixed(digits)}%` : n.toFixed(digits);
};
const metric = (bags, cat, name) => {
    const items = bags?.[cat] || [];
    return items.find((m) => m.metric_name === name);
};

const AddSlot = ({ onPick, allSifs, disabledIds }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const filtered = allSifs.filter((s) => !disabledIds.includes(s.id) && (!q || (s.sif_name + s.sif_code).toLowerCase().includes(q.toLowerCase())));
    return (
        <div className="rounded-2xl border border-dashed border-oth-line bg-white p-5 h-full flex flex-col items-center justify-center min-h-[220px]" data-testid="sc-compare-slot-empty">
            {!open ? (
                <button onClick={() => setOpen(true)} className="btn-pill btn-pill-dark !text-xs" data-testid="sc-compare-add">
                    <Plus size={14} /> Add SIF
                </button>
            ) : (
                <div className="w-full">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search…"
                        autoFocus
                        className="w-full h-9 px-3 rounded-full border border-oth-line bg-white text-xs focus:outline-none focus:border-oth-ink"
                        data-testid="sc-compare-search"
                    />
                    <div className="mt-2 max-h-56 overflow-y-auto space-y-1 thin-scroll">
                        {filtered.slice(0, 25).map((s) => (
                            <button
                                key={s.id}
                                onClick={() => { onPick(s.id); setOpen(false); setQ(""); }}
                                className="w-full text-left px-3 py-2 rounded-lg hover:bg-oth-bg-2 text-xs font-body"
                                data-testid={`sc-compare-pick-${s.id}`}
                            >
                                <div className="text-oth-ink truncate">{s.sif_name}</div>
                                <div className="text-oth-ink3 text-[10px]">{s.sif_code}</div>
                            </button>
                        ))}
                        {filtered.length === 0 && <div className="text-[11px] font-body text-oth-ink3 text-center py-3">No matches</div>}
                    </div>
                    <button onClick={() => setOpen(false)} className="mt-2 text-[11px] font-body text-oth-ink3 hover:text-oth-ink w-full">Cancel</button>
                </div>
            )}
        </div>
    );
};

const Card = ({ d, onRemove }) => {
    const s = d.sif;
    const amc = d.amc || {};
    const rating = (d.ratings || []).find((r) => r.status === "ranked") || (d.ratings || [])[0];
    const groups = d.metrics_by_category || {};
    const rowsReturns = [
        ["1Y return", metric(groups, "returns", "return_1y"), "%"],
        ["3Y CAGR", metric(groups, "returns", "cagr_3y"), "%"],
        ["Since inception", metric(groups, "returns", "cagr_si"), "%"],
    ];
    const rowsRisk = [
        ["Volatility", metric(groups, "risk", "volatility"), "%"],
        ["Sharpe", metric(groups, "risk_adjusted", "sharpe"), ""],
        ["Sortino", metric(groups, "risk_adjusted", "sortino"), ""],
        ["Max drawdown", metric(groups, "risk", "max_drawdown"), "%"],
        ["Calmar", metric(groups, "risk_adjusted", "calmar"), ""],
    ];
    const rowsCons = [
        ["Positive months", metric(groups, "consistency", "positive_months_pct"), "%"],
        ["Rolling 1Y positive", metric(groups, "consistency", "rolling_1y_positive_pct"), "%"],
    ];
    const Row = ([label, m, unit]) => (
        <div key={label} className="flex items-center justify-between py-1.5 border-b border-oth-line/50 last:border-b-0">
            <div className="text-[11px] font-body text-oth-ink3">{label}</div>
            <div className={`text-xs font-body ${m?.eligible ? "text-oth-ink" : "text-oth-ink3"}`}>{m?.eligible ? fmt(m.value, unit) : "—"}</div>
        </div>
    );
    return (
        <div className="rounded-2xl border border-oth-line bg-white p-5 relative" data-testid={`sc-compare-card-${s.id}`}>
            <button onClick={onRemove} className="absolute top-3 right-3 w-7 h-7 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" data-testid={`sc-compare-remove-${s.id}`}>
                <X size={13} />
            </button>
            <div className="pr-8">
                <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{amc.amc_name || "—"}</div>
                <div className="mt-0.5 font-display text-base font-semibold text-oth-ink leading-tight">{s.sif_name}</div>
                <div className="text-[11px] font-body text-oth-ink3 mt-1">{s.sif_code} · {s.strategy_category || "—"}</div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-oth-ink3 font-body">NAV</div>
                    <div className="text-sm font-display font-semibold text-oth-ink mt-0.5">{d.nav_latest ? Number(d.nav_latest.nav).toFixed(4) : "—"}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-oth-ink3 font-body">TER</div>
                    <div className="text-sm font-display font-semibold text-oth-ink mt-0.5">{d.ter_latest ? `${Number(d.ter_latest.ter).toFixed(2)}%` : "—"}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-oth-ink3 font-body">Rating</div>
                    <div className="text-sm font-display font-semibold text-oth-ink mt-0.5">{rating?.star_label || "—"}</div>
                </div>
            </div>
            <div className="mt-5 space-y-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1">Returns</div>
                {rowsReturns.map(Row)}
            </div>
            <div className="mt-4 space-y-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1">Risk</div>
                {rowsRisk.map(Row)}
            </div>
            <div className="mt-4 space-y-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1">Consistency</div>
                {rowsCons.map(Row)}
            </div>
        </div>
    );
};

export default function SifControlCompare() {
    const [allSifs, setAllSifs] = useState([]);
    const [ids, setIds] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        scListSifs({ limit: 200 }).then((r) => setAllSifs(r.items || [])).catch(() => setAllSifs([]));
    }, []);
    useEffect(() => {
        if (ids.length === 0) { setItems([]); return; }
        setLoading(true);
        scCompare(ids).then((r) => setItems(r.items || [])).finally(() => setLoading(false));
    }, [ids]);

    const slots = 4;
    const add = (sifId) => setIds((prev) => (prev.length >= slots ? prev : [...prev, sifId]));
    const remove = (sifId) => setIds((prev) => prev.filter((x) => x !== sifId));

    return (
        <section className="py-14 md:py-20" data-testid="sc-compare-page">
            <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-oth-ink3 font-body">Compare</div>
                <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-oth-ink">Compare up to four SIFs</h1>
                <p className="mt-4 max-w-[620px] text-oth-ink2 font-body text-base md:text-lg">Side-by-side identity, returns, risk, consistency and rating. Pick from any AMC in the master.</p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sc-compare-grid">
                    {[...Array(slots)].map((_, i) => {
                        const id = ids[i];
                        const filled = items.find((it) => it.sif.id === id);
                        if (filled) return <Card key={id} d={filled} onRemove={() => remove(id)} />;
                        if (id && loading) return <div key={id} className="rounded-2xl border border-oth-line bg-white p-5 min-h-[220px] flex items-center justify-center text-xs font-body text-oth-ink3">Loading…</div>;
                        return <AddSlot key={`slot-${i}`} onPick={add} allSifs={allSifs} disabledIds={ids} />;
                    })}
                </div>

                <div className="mt-4 text-[11px] font-body text-oth-ink3">
                    Metrics marked "—" indicate the SIF's history is too short for that metric. As daily NAV ingestion continues, more values will populate.
                </div>
            </div>
        </section>
    );
}
